import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, CheckCircle2, ChevronRight } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { WhatsAppButton } from "@/components/landing/WhatsAppButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePageMeta, SITE_URL } from "@/seo/usePageMeta";
import { SERVICES_DATA } from "@/data/servicesData";

const slideUp = {
  initial: { y: 24 },
  whileInView: { y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" },
};

const SectionHeading = ({ overline, title }) => (
  <motion.div {...slideUp}>
    <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-brand-primary mb-4">{overline}</p>
    <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter leading-none text-zinc-900">{title}</h2>
    <motion.div
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="origin-left mt-5 h-1 w-20 rounded-full bg-gradient-to-r from-[#8C6D1F] via-[#D4AF37] to-[#E9C767]"
    />
  </motion.div>
);

const WHY_POINTS = [
  "Local New Brunswick team that answers the phone",
  "Authorized Verkada Dealer — commercial-grade equipment",
  "3-year warranty on equipment and workmanship",
  "Honest, written quotes — no surprise charges",
  "One team for security, cabling, AV and networking",
  "Support after installation, not just during",
];

const PROCESS = [
  { title: "Free On-Site Quote", desc: "We visit your property, listen to what you need, and give you a written quote — no obligation." },
  { title: "System Design", desc: "Equipment and placement planned around your property, with clear pricing before work starts." },
  { title: "Professional Installation", desc: "Clean mounting, concealed wiring, and full setup — typically in a single visit." },
  { title: "Training & Support", desc: "We show you how everything works and back the job with our 3-year warranty." },
];

const INSTALL_IMAGES = [
  { url: "/assets/install-house.webp", alt: "PTZ security cameras installed by SafeHome on a brick suburban home" },
  { url: "/assets/install-bullet.webp", alt: "SafeHome-installed 4K bullet security camera mounted on a brick exterior" },
  { url: "/assets/gallery-patch.webp", alt: "Patch panel and network rack installation by SafeHome technicians" },
];

export default function CityPage({ city }) {
  usePageMeta({
    title: city.metaTitle,
    description: city.metaDescription,
    path: `/service-areas/${city.slug}/`,
  });

  useEffect(() => {
    const id = "city-schema";
    document.getElementById(id)?.remove();
    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: `Security & Technology Installation in ${city.name}`,
        description: city.metaDescription,
        url: `${SITE_URL}/service-areas/${city.slug}/`,
        areaServed: { "@type": "City", name: `${city.name}, New Brunswick` },
        provider: {
          "@type": "HomeAndConstructionBusiness",
          name: "SafeHome Security Solutions Inc.",
          telephone: "+1-506-871-4144",
          url: `${SITE_URL}/`,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Service Areas", item: `${SITE_URL}/#top` },
          { "@type": "ListItem", position: 3, name: city.name, item: `${SITE_URL}/service-areas/${city.slug}/` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: city.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ]);
    document.head.appendChild(script);
    return () => document.getElementById(id)?.remove();
  }, [city]);

  const services = SERVICES_DATA.filter((s) => city.services.some((c) => c.slug === s.slug));

  return (
    <div data-testid={`city-page-${city.slug}`}>
      <Header />
      <main>
        <section className="relative grain">
          <div className="absolute inset-0">
            <img src={city.image} alt={city.imageAlt} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/60" />
          </div>
          <div className="relative container-x py-24 lg:py-32">
            <nav data-testid="city-breadcrumb" className="flex items-center gap-1.5 text-xs sm:text-sm text-white/70 mb-8" aria-label="Breadcrumb">
              <Link to="/" data-testid="city-breadcrumb-home" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span>Service Areas</span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white">{city.name}</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-none text-white max-w-3xl">
              {city.h1}
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/85 max-w-2xl">{city.intro}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/#quote"
                data-testid="city-quote-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-brand-primary bg-brand-primary-hover text-black font-semibold transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#D4AF37]/25"
              >
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+15068714144"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-white/10 backdrop-blur-md border border-white/30 text-white font-semibold transition-all duration-200 hover:-translate-y-1 hover:bg-white/20"
              >
                <Phone className="w-4 h-4" /> (506) 871-4144
              </a>
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28">
          <div className="container-x">
            <SectionHeading overline={`Serving ${city.name}`} title={`Services available in ${city.name}`} />
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-x-clip">
              {city.services.map((cs, i) => {
                const service = services.find((s) => s.slug === cs.slug);
                return (
                  <motion.div
                    key={cs.slug}
                    initial={{ x: i % 2 === 0 ? -24 : 24 }}
                    whileInView={{ x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  >
                    <Link
                      to={`/services/${cs.slug}/`}
                      data-testid={`city-service-${cs.slug}`}
                      className="group block h-full bg-white border border-zinc-200 rounded-md p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/60 hover:shadow-[0_8px_30px_rgba(212,175,55,0.18)]"
                    >
                      <h3 className="text-lg font-bold tracking-tight text-zinc-900 group-hover:text-brand-primary transition-colors">
                        {service.name}
                      </h3>
                      <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{cs.blurb}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary">
                        Learn more <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28 bg-[#F5F5F4]">
          <div className="container-x">
            <SectionHeading overline="Who We Help" title={`Residential & commercial work in ${city.name}`} />
            <div className="mt-12 grid md:grid-cols-2 gap-6">
              {[
                { title: "Residential", items: city.applications.residential },
                { title: "Commercial", items: city.applications.commercial },
              ].map((group) => (
                <div key={group.title} className="bg-white border border-zinc-200 rounded-md p-8 shadow-sm">
                  <h3 className="text-xl font-bold tracking-tight text-zinc-900">{group.title}</h3>
                  <ul className="mt-5 space-y-3">
                    {group.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-zinc-700">
                        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-[#D4AF37]" />
                        <span className="text-sm sm:text-base">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28">
          <div className="container-x">
            <SectionHeading overline="Our Work" title="Recent SafeHome installations" />
            <p className="mt-6 text-sm sm:text-base text-zinc-600 max-w-2xl">
              A look at our workmanship — real SafeHome installations from across New Brunswick.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {INSTALL_IMAGES.map((img) => (
                <figure key={img.url} className="relative overflow-hidden rounded-xl border border-zinc-200 aspect-[4/3]">
                  <img src={img.url} alt={img.alt} loading="lazy" className="w-full h-full object-cover" />
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28 bg-[#F5F5F4]">
          <div className="container-x grid lg:grid-cols-2 gap-14">
            <div>
              <SectionHeading overline="Why SafeHome" title={`Why ${city.name} chooses SafeHome`} />
              <ul className="mt-10 space-y-4">
                {WHY_POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-zinc-700">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-[#D4AF37]" />
                    <span className="text-sm sm:text-base leading-relaxed">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <SectionHeading overline="How It Works" title="Our installation process" />
              <div className="mt-10 space-y-5">
                {PROCESS.map((step, i) => (
                  <div key={step.title} className="flex gap-4 bg-white border border-zinc-200 rounded-md p-6 shadow-sm">
                    <span className="inline-flex items-center justify-center w-10 h-10 shrink-0 rounded-md bg-brand-primary text-black font-extrabold">
                      {i + 1}
                    </span>
                    <div>
                      <h3 className="text-base font-bold tracking-tight text-zinc-900">{step.title}</h3>
                      <p className="mt-1 text-sm text-zinc-600 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28">
          <div className="container-x max-w-4xl">
            <SectionHeading overline="Questions" title={`${city.name} FAQs`} />
            <Accordion type="single" collapsible className="mt-10">
              {city.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`city-faq-${city.slug}-${i}`}>
                  <AccordionTrigger className="text-left text-zinc-900 font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-zinc-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="py-20 lg:py-28 bg-[#F5F5F4]">
          <div className="container-x">
            <SectionHeading overline="Service Area" title={`Proudly serving ${city.name} & nearby communities`} />
            <div className="mt-10 mx-auto w-[80%] rounded-xl overflow-hidden border border-zinc-200 shadow-sm aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9]">
              <iframe
                src={city.mapSrc}
                title={`Map of the ${city.name}, New Brunswick service area`}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="w-full h-full border-0"
              />
            </div>
            <p className="mt-4 text-xs text-zinc-500">
              We come to you — SafeHome serves {city.name} and surrounding communities across New Brunswick.
            </p>
          </div>
        </section>

        <section className="py-16 lg:py-20 bg-black grain relative">
          <div className="container-x flex flex-wrap items-center justify-between gap-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-white">
                Get a free quote in {city.name}
              </h2>
              <p className="mt-3 text-white/70 text-base">Free on-site quotes — call us or send the form.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/#quote"
                data-testid="city-cta-quote-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-brand-primary bg-brand-primary-hover text-black font-semibold transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#D4AF37]/25"
              >
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+15068714144"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-white/10 border border-white/25 text-white font-semibold transition-all duration-200 hover:-translate-y-1 hover:bg-white/20"
              >
                <Phone className="w-4 h-4" /> (506) 871-4144
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
